import React, { Component, useRef } from 'react';
import DocViewer, {
  DocViewerRef,
  DocViewerRenderers
} from '@cyntler/react-doc-viewer';
import { HtmlIframeViewer } from 'components/HtmlIframeViewer';
import { Box, SxProps, Typography } from '@mui/material';
import { EmptyState } from 'system/atoms';
import './styles.css';
import '@cyntler/react-doc-viewer/dist/index.css';

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

const errorContainerSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: '200px',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 3
};

class FileViewerErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={errorContainerSx}>
          <Typography variant="body2" color="text.secondary">
            Unable to preview this document. The file may be empty or corrupted.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

type Props = {
  docs: {
    uri: string;
    fileName?: string;
    fileType?: string;
  }[];
  style?: React.CSSProperties;
};

const FilesViewerComponent: React.FC<Props> = ({ docs, style }) => {
  const docViewerRef = useRef<DocViewerRef>(null);

  if (!docs.length || !docs[0]?.uri) {
    return (
      <EmptyState
        type="no-records"
        title="No document available"
        subtitle="No document available to preview."
      />
    );
  }

  const isHtmlFile =
    docs[0]?.fileType === 'html' ||
    docs[0]?.fileName?.toLowerCase().endsWith('.html');

  if (isHtmlFile) {
    return (
      <HtmlIframeViewer
        url={docs[0].uri}
        title={docs[0].fileName || 'Document'}
        style={{ height: '100%', ...style }}
      />
    );
  }

  return (
    <FileViewerErrorBoundary>
      <DocViewer
        ref={docViewerRef}
        documents={docs}
        initialActiveDocument={docs[1]}
        // NOTE: DOCX Isn't supported
        pluginRenderers={DocViewerRenderers}
        style={style}
        config={{
          pdfVerticalScrollByDefault: true,
          header: {
            disableHeader: true,
            disableFileName: true
          },
          pdfZoom: {
            defaultZoom: 1.13,
            zoomJump: 0.1
          }
        }}
      />
    </FileViewerErrorBoundary>
  );
};

export const FilesViewer = React.memo(
  FilesViewerComponent,
  (prevProps, nextProps) => {
    return prevProps.docs[0]?.uri === nextProps.docs[0]?.uri;
  }
);
